import React, { useState } from "react";
import { getDisplayPlatforms } from "../utils/networkNames";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { ExternalLink, Share2, Play, ChevronDown, ChevronRight, CalendarDays } from "lucide-react";
import Typography from "./ui/Typography";
import Button from "./ui/Button";
import Chip from "./ui/Chip";
import PosterPlaceholder from "./ui/PosterPlaceholder";
import RecommendationRibbon from "./RecommendationRibbon";
import SeasonBadge from "./ui/SeasonBadge";
import RatingBadge from "./ui/RatingBadge";
import RatingTooltipBadge from "./ui/RatingTooltipBadge";
import WatchlistHeartButton from "./ui/WatchlistHeartButton";
import DiscoveryCue from "./DiscoveryCue";
import { cn } from "../utils/classNames";
import { formatReleaseDate } from "../utils/formatReleaseDate";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { BUTTON_LABELS } from "../constants/buttonLabels";
import { shareShow } from "../utils/share";
import { handleInternalLinkClick } from "../utils/navigation";
import ShowsLikeLink from "./ShowsLikeLink";
import { getContentRating, normalizeSeasonNumber } from "../utils/showFields";
import { getPrimaryRating } from "../utils/showRatings";
import { trackEvent } from "../utils/analytics";
import { useTextTruncation } from "../hooks/useTextTruncation";

const NewShowCard = React.forwardRef(
  ({
    release,
    className,
    density = "comfortable",
    recommendationVariant = "none",
    priority = false,
    highlighted = false,
    isInWatchlist,
    onToggleWatchlist,
    ...props
  }, ref) => {
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [failedPosterSrc, setFailedPosterSrc] = useState(null);
    const prefersReducedMotion = usePrefersReducedMotion();
    const inWatchlist = isInWatchlist?.(release);
    const contentRating = getContentRating(release);
    const seasonNumber = normalizeSeasonNumber(release.seasonNumber);
    const trailerUrl = release.trailers?.find((trailer) => trailer?.url)?.url || null;

    // Get primary network
    const primaryNetwork = getDisplayPlatforms(release);

    // Get display genres (limit to first 2)
    const displayGenres = release.genres?.slice(0, 2) || [];
    const metadataLine = [primaryNetwork, ...displayGenres].filter(Boolean).join(" / ");

    // Description text
    const description = release.plotOverview?.trim() || "No summary available.";
    const hasDescription = description !== "No summary available.";
    const { ref: summaryRef, isTruncated: isSummaryTruncated } = useTextTruncation(
      [description, density],
      { disabled: isSummaryExpanded || !hasDescription }
    );

    const hoverShadow = density === "compact" ? "var(--shadow-card-dense-hover)" : "var(--shadow-card-hover)";
    const posterSrc = release.poster || null;
    const posterThumb = release.posterMetadata?.thumbnail || null;
    const posterFailed = posterSrc && failedPosterSrc === posterSrc;
    const nextWatch = release.nextWatch?.slug && release.nextWatch?.title ? release.nextWatch : null;
    const showRecommendationRibbon = recommendationVariant === "poster-ribbon" && nextWatch;
    const showPath = release.slug ? `/show/${release.slug}` : null;

    return (
      <motion.div
        ref={ref}
        id={`show-${release.id}`}
        className={cn(
          "group bg-card text-card-foreground border border-accent-gold/70 rounded-xl overflow-hidden",
          "relative transition-all duration-200 h-full flex flex-col",
          highlighted && "random-pick-highlight",
          className
        )}
        initial={{ y: 0, rotateX: 0, boxShadow: "none" }}
        animate={
          isHovered
            ? {
                y: prefersReducedMotion ? 0 : -6,
                rotateX: prefersReducedMotion ? 0 : 2,
                boxShadow: hoverShadow,
              }
            : { y: 0, rotateX: 0, boxShadow: "var(--shadow-card-base)" }
        }
        transition={{
          duration: 0.25,
          ease: [0.0, 0.0, 0.2, 1],
          boxShadow: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1] },
        }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        {...props}
      >
        {/* "NEW SERIES" badge */}
        {release.seasonNumber === 1 && !release.seasonPart && (
          <RatingBadge
            variant="new"
            className="absolute top-2 right-2 z-10"
          >
            NEW
          </RatingBadge>
        )}

        {/* Poster Image */}
        <div className="aspect-[2/3] relative overflow-hidden border-b border-accent-gold/60">
          <a
            href={showPath || undefined}
            onClick={showPath ? (event) => handleInternalLinkClick(event, showPath) : undefined}
            className={cn(
              "block h-full w-full focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card",
              !showPath && "pointer-events-none"
            )}
            aria-label={showPath ? `View details for ${release.title}` : undefined}
            tabIndex={showPath ? undefined : -1}
          >
            {posterSrc && !posterFailed ? (
              <>
                <img
                  src={posterSrc}
                  srcSet={posterThumb ? `${posterThumb} 340w, ${posterSrc} 680w` : undefined}
                  sizes="(max-width: 640px) 160px, (max-width: 1024px) 200px, 250px"
                  alt={`${release.title} poster`}
                  className="w-full h-full object-cover grayscale-[20%] contrast-110 transition-transform duration-200 group-hover:scale-105"
                  loading="lazy"
                  fetchPriority={priority ? "high" : "auto"}
                  onError={() => setFailedPosterSrc(posterSrc)}
                />
                {/* Ink press effect overlay */}
                <div className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-black/30 mix-blend-multiply" />
              </>
            ) : (
              <PosterPlaceholder
                showId={release.id}
                className="w-full h-full bg-poster-placeholder-gradient"
              />
            )}
          </a>
          {showRecommendationRibbon && (
            <RecommendationRibbon
              nextWatch={nextWatch}
              anchorTitle={release.title}
              overlay
              className="absolute inset-x-3 bottom-3 z-10"
            />
          )}
        </div>

        {/* Content */}
        <div className={cn(
          "bg-card flex flex-col flex-1 p-4 sm:p-5",
          density === "compact" ? "gap-2" : "gap-2.5"
        )}>
          {/* Title - Editorial style */}
          <div className="show-card-title-row flex items-start justify-between gap-3">
            <Typography
              as="h3" variant="h4"
              className={cn(
                "min-w-0 leading-tight line-clamp-2 font-display",
                density === "compact" && "text-body"
              )}
            >
              {showPath ? (
                <a
                  href={showPath}
                  onClick={(e) => handleInternalLinkClick(e, showPath)}
                  className="title-link"
                >
                  {release.title}
                </a>
              ) : (
                release.title
              )}
            </Typography>
            <div className="flex shrink-0 items-center gap-1">
              <WatchlistHeartButton show={release} inWatchlist={Boolean(inWatchlist)} onToggle={onToggleWatchlist} />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  shareShow(release);
                }}
                className="sp-card-icon-action"
                aria-label={`Share ${release.title}`}
              >
                <Share2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 border-y border-border/70 py-2">
            <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
              {getPrimaryRating(release) !== null ? (
                <RatingTooltipBadge show={release} tooltipClassName="max-w-sm" />
              ) : null}
              <SeasonBadge seasonNumber={seasonNumber} part={release.seasonPart} />
              {contentRating && (
                <Chip
                  variant="outline"
                  size="badge"
                  className="text-meta text-muted-foreground"
                >
                  {contentRating}
                </Chip>
              )}
              {release.episodeCount && (
                <Chip
                  variant="outline"
                  size="badge"
                  className="text-meta text-muted-foreground"
                >
                  {release.episodeCount} eps
                </Chip>
              )}
            </div>

            <div className="sp-card-date-meta">
              <CalendarDays className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              <span>{formatReleaseDate(release.releaseDate)}</span>
            </div>

            {metadataLine && (
              <Typography variant="caption" className="line-clamp-1 text-muted-foreground">
                {metadataLine}
              </Typography>
            )}

          </div>

          <DiscoveryCue
            cue={release.discoveryCue}
            currentSlug={release.slug || ""}
            currentTitle={release.title || ""}
            surface="show_card"
            compact={density === "compact"}
          />

          {/* Summary */}
          <div className="relative">
            <Typography
              ref={summaryRef}
              variant="body"
              className={cn(
                "text-muted-foreground leading-relaxed break-words",
                isSummaryTruncated && "pr-7",
                isSummaryExpanded
                  ? density === "compact"
                    ? "text-body-small"
                    : null
                  : density === "compact"
                    ? "show-card-summary--compact line-clamp-3 text-body-small"
                    : "show-card-summary--comfortable line-clamp-2"
              )}
            >
              {description}
            </Typography>

            {isSummaryTruncated && (
              <button
                type="button"
                className="sp-card-summary-toggle focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-secondary"
                aria-expanded={isSummaryExpanded}
                aria-label={isSummaryExpanded ? "Collapse summary" : "Expand summary"}
                onClick={() => setIsSummaryExpanded((current) => !current)}
              >
                {isSummaryExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </button>
            )}
          </div>

          {/* Quiet external links keep the internal show page as the primary path. */}
          {(release.detailsUrl || trailerUrl || release.hasShowsLikePage) && (
            <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
              <ShowsLikeLink show={release} surface="show_card" />
              {release.detailsUrl && (
                <a
                  href={release.detailsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center gap-1.5 text-body-xs text-label-light text-muted-foreground transition-colors hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    trackEvent("outbound_click", { surface: "show_card", type: "details", slug: release.slug });
                  }}
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  {BUTTON_LABELS.QUERY_IMDB}
                </a>
              )}
              {trailerUrl && (
                <a
                  href={trailerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center gap-1.5 text-body-xs text-label-light text-muted-foreground transition-colors hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    trackEvent("outbound_click", { surface: "show_card", type: "trailer", slug: release.slug });
                  }}
                >
                  <Play className="h-3.5 w-3.5" aria-hidden="true" />
                  {BUTTON_LABELS.WATCH_TRAILER}
                </a>
              )}
            </div>
          )}

        </div>
      </motion.div>
    );
  }
);

NewShowCard.displayName = "NewShowCard";

NewShowCard.propTypes = {
  release: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    rating: PropTypes.number,
    poster: PropTypes.string,
    posterMetadata: PropTypes.shape({
      thumbnail: PropTypes.string,
    }),
    seasonNumber: PropTypes.number,
    seasonPart: PropTypes.string,
    releaseDate: PropTypes.string.isRequired,
    sourceName: PropTypes.string,
    year: PropTypes.number,
    endYear: PropTypes.number,
    genres: PropTypes.arrayOf(PropTypes.string),
    networks: PropTypes.arrayOf(PropTypes.string),
    plotOverview: PropTypes.string,
    contentRating: PropTypes.string,
    episodeCount: PropTypes.number,
    trailers: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      name: PropTypes.string,
    })),
    detailsUrl: PropTypes.string,
    slug: PropTypes.string,
    hasShowsLikePage: PropTypes.bool,
    nextWatch: PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
      bridge: PropTypes.string,
    }),
    discoveryCue: PropTypes.shape({
      type: PropTypes.oneOf(["inbound", "taste"]),
      label: PropTypes.string,
      anchor: PropTypes.shape({
        slug: PropTypes.string,
        title: PropTypes.string,
      }),
      reasons: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  className: PropTypes.string,
  density: PropTypes.oneOf(["comfortable", "compact"]),
  recommendationVariant: PropTypes.oneOf(["none", "poster-ribbon"]),
  priority: PropTypes.bool,
  highlighted: PropTypes.bool,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
};

export default NewShowCard;
